import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import '@/index.css';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { FavoriteBetsProvider } from './contexts/FavoriteBetsContext';
import { GestaoDockProvider } from './contexts/GestaoDockContext';
import { OpenAccessPromoProvider } from './contexts/OpenAccessPromoContext';
import GestaoFloatingDock from './components/gestao/GestaoFloatingDock';

/**
 * Code-splitting por rota: cada página é um chunk separado, baixado apenas
 * quando o utilizador acede à rota correspondente. Resultado: bundle inicial
 * muito menor (a home deixa de pagar pelo bundle de todas as outras páginas).
 */
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const MatchAnalysisPage = lazy(() => import('./pages/MatchAnalysisPage'));
const ExpertFeedPage = lazy(() => import('./pages/ExpertFeedPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const UpcomingMatchesPage = lazy(() => import('./pages/UpcomingMatchesPage'));
const LivescoresPage = lazy(() => import('./pages/LivescoresPage'));
const LiveMatchDetailPage = lazy(() => import('./pages/LiveMatchDetailPage'));
const FeaturedBetsPage = lazy(() => import('./pages/FeaturedBetsPage'));
const PlayerProfilePage = lazy(() => import('./pages/PlayerProfilePage'));
const PlayerComparePage = lazy(() => import('./pages/PlayerComparePage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
const LeaguePage = lazy(() => import('./pages/LeaguePage'));
const GestaoPage = lazy(() => import('./pages/GestaoPage'));

/** Spinner discreto enquanto o chunk da rota é descarregado (primeiro acesso). */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/sobre" element={<Navigate to="/landing#sobre-nos" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/" element={<Navigate to="/landing" replace />} />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team/:teamId"
          element={
            <ProtectedRoute>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/match-analysis"
          element={
            <ProtectedRoute>
              <MatchAnalysisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <ExpertFeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowExpired>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <LeaderboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestao"
          element={
            <ProtectedRoute>
              <GestaoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/predictions"
          element={
            <ProtectedRoute>
              <Navigate to="/leaderboard?tab=palpites" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/matches"
          element={
            <ProtectedRoute>
              <UpcomingMatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/livescores"
          element={
            <ProtectedRoute>
              <LivescoresPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/livescores/:fixtureId"
          element={
            <ProtectedRoute>
              <LiveMatchDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bets"
          element={
            <ProtectedRoute>
              <FeaturedBetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/players"
          element={
            <ProtectedRoute>
              <Navigate to="/home" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/player/:playerId"
          element={
            <ProtectedRoute>
              <PlayerProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/player-compare"
          element={
            <ProtectedRoute>
              <PlayerComparePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans"
          element={
            <ProtectedRoute allowExpired>
              <PlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/liga/:slug"
          element={
            <ProtectedRoute>
              <LeaguePage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function AppContent() {
  const location = useLocation();
  const path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const hideFooter =
    path === '/login' ||
    path === '/forgot-password' ||
    path === '/reset-password' ||
    path === '/landing' ||
    path === '/' ||
    location.hash?.includes('session_id=');
  return (
    <>
      <main>
        <AppRouter />
      </main>
      {!hideFooter && <Footer />}
      <GestaoFloatingDock />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <FavoriteBetsProvider>
              <GestaoDockProvider>
                <OpenAccessPromoProvider>
                  <AppContent />
                </OpenAccessPromoProvider>
              </GestaoDockProvider>
            </FavoriteBetsProvider>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
